import React from 'react'
import { Trans } from '@lingui/macro'
import { OfflineNavIcon } from './offline-nav-icon'
import { canProvisionDevice } from '../../offline/device-provision-meta'
import { canExportLocalCrashData } from '../../offline/offline-field-permissions'
import {
  canImportCrashZipPackage,
  canViewPackageImportsList,
} from '../../offline/package-exports-permissions'

export const OFFLINE_DATA_NAV_ID = '__offline_data__'

/**
 * @param {number} userType
 * @param {boolean} isOnline
 * @returns {boolean}
 */
export function shouldShowOfflineDataSection(userType, isOnline) {
  const canShowPrepareDevice = isOnline && canProvisionDevice(userType)
  const canImportCrashZipArchive = canImportCrashZipPackage(userType)
  return (
    (canExportLocalCrashData(userType) && !isOnline) ||
    canImportCrashZipArchive ||
    canShowPrepareDevice
  )
}

/**
 * @returns {{ route: string, label: React.ReactNode, icon: React.ReactNode, subPanelsNumber: number, access: number[], isOfflineDataSection: boolean }}
 */
export function getOfflineDataNavItem() {
  return {
    route: OFFLINE_DATA_NAV_ID,
    label: <Trans>Offline data</Trans>,
    icon: <OfflineNavIcon variant="offline" />,
    subPanelsNumber: 1,
    access: [0, 1, 2, 3],
    isOfflineDataSection: true,
  }
}

/**
 * @param {Object} params
 * @param {number} params.userType
 * @param {boolean} params.isOnline
 * @param {{ onExport?: () => void, onImportZip?: () => void, importZipBusy?: boolean } | null} params.actions
 * @param {(path: string, options?: object) => void} params.navigate
 * @param {object} params.locationState
 * @param {string} params.pathname
 * @returns {Array<{ key: string, label: React.ReactNode, icon: React.ReactNode, disabled: boolean, active: boolean, action: () => void }>}
 */
export function getOfflineDataSubItems({
  userType,
  isOnline,
  actions,
  navigate,
  locationState,
  pathname,
}) {
  const items = []
  const canShowPrepareDevice = isOnline && canProvisionDevice(userType)
  const canImportCrashZipArchive = canImportCrashZipPackage(userType)

  if (canShowPrepareDevice) {
    items.push({
      key: 'prepare',
      label: <Trans>Prepare device offline</Trans>,
      icon: <OfflineNavIcon variant="prepare" />,
      active: pathname === '/device-provision',
      disabled: false,
      action: () =>
        navigate('/device-provision', {
          state: { ...locationState, isHistoryOpen: false },
        }),
    })
  }

  if (canExportLocalCrashData(userType) && shouldShowOfflineDataSection(userType, isOnline)) {
    items.push({
      key: 'export',
      label: <Trans>Export local data</Trans>,
      icon: <OfflineNavIcon variant="export" />,
      active: false,
      disabled: typeof actions?.onExport !== 'function',
      action: () => actions?.onExport?.(),
    })
  }

  if (
    canImportCrashZipArchive &&
    isOnline &&
    typeof actions?.onImportZip === 'function'
  ) {
    items.push({
      key: 'import',
      label: <Trans>Import local data</Trans>,
      icon: <OfflineNavIcon variant="import" />,
      active: false,
      disabled: !!actions.importZipBusy,
      action: actions.onImportZip,
    })
  }

  if (canViewPackageImportsList(userType) && isOnline) {
    items.push({
      key: 'import-history',
      label: <Trans>Import history</Trans>,
      icon: <OfflineNavIcon variant="history" />,
      active: pathname === '/package-imports',
      disabled: false,
      action: () =>
        navigate('/package-imports', {
          state: { ...locationState, isHistoryOpen: false },
        }),
    })
  }

  return items
}
